export const CHANNEL_TYPES = {
  DUTY_FREE: "duty_free",
  OFFICIAL: "official",
  BRAND_AUTHORIZED: "brand_authorized",
  SELF_OPERATED: "self_operated",
  THIRD_PARTY: "third_party",
} as const;

export type ChannelType = (typeof CHANNEL_TYPES)[keyof typeof CHANNEL_TYPES];

export const TRUST_LEVELS = {
  OFFICIAL: "official",
  HIGH: "high",
  MEDIUM: "medium",
  UNKNOWN: "unknown",
} as const;

export type TrustLevel = (typeof TRUST_LEVELS)[keyof typeof TRUST_LEVELS];

export const TRUST_LEVEL_RANK: Record<TrustLevel, number> = {
  official: 4,
  high: 3,
  medium: 2,
  unknown: 1,
};

export const STOCK_STATUSES = {
  IN_STOCK: "in_stock",
  LOW_STOCK: "low_stock",
  OUT_OF_STOCK: "out_of_stock",
  UNKNOWN: "unknown",
} as const;

export type StockStatus = (typeof STOCK_STATUSES)[keyof typeof STOCK_STATUSES];

export const PRODUCT_CATEGORIES = {
  SKINCARE: "skincare",
  MAKEUP: "makeup",
  FRAGRANCE: "fragrance",
  SET: "set",
  BODY: "body",
  HAIR: "hair",
  LUXURY: "luxury",
} as const;

export type ProductCategory =
  (typeof PRODUCT_CATEGORIES)[keyof typeof PRODUCT_CATEGORIES];

export const ENTITY_STATUSES = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  ARCHIVED: "archived",
} as const;

export type EntityStatus = (typeof ENTITY_STATUSES)[keyof typeof ENTITY_STATUSES];

export const OUTBOUND_CLICK_STATUSES = {
  PENDING: "pending",
  SUCCESS: "success",
  FAILED: "failed",
} as const;

export type OutboundClickStatus =
  (typeof OUTBOUND_CLICK_STATUSES)[keyof typeof OUTBOUND_CLICK_STATUSES];

export const OUTBOUND_CLICK_SCENES = {
  PRODUCT_DETAIL: "product_detail",
  SEARCH_RESULT: "search_result",
  FAVORITES: "favorites",
  HOME: "home",
} as const;

export type OutboundClickScene =
  (typeof OUTBOUND_CLICK_SCENES)[keyof typeof OUTBOUND_CLICK_SCENES];

export const AFFILIATE_NETWORK_TYPES = {
  DUOMAI: "duomai",
  TENCENT_YOUXUAN: "tencent_youxuan",
} as const;

export type AffiliateNetworkType =
  (typeof AFFILIATE_NETWORK_TYPES)[keyof typeof AFFILIATE_NETWORK_TYPES];

/**
 * Chinese labels for the affiliate networks. Admin UI uses these for the
 * network 类型 column / select; keeping them next to the enum so adding a new
 * network forces both sides of the wire to stay in sync.
 */
export const AFFILIATE_NETWORK_LABELS_CN: Record<AffiliateNetworkType, string> = {
  duomai: "多麦联盟",
  tencent_youxuan: "腾讯云选联盟",
};

export const AFFILIATE_ORDER_STATUSES = {
  PENDING: "pending",
  CONFIRMED: "confirmed",
  SETTLED: "settled",
  REFUNDED: "refunded",
  CANCELLED: "cancelled",
} as const;

export type AffiliateOrderStatus =
  (typeof AFFILIATE_ORDER_STATUSES)[keyof typeof AFFILIATE_ORDER_STATUSES];

export const SORT_OPTIONS = {
  RECOMMENDED: "recommended",
  LOWEST_PRICE: "lowest_price",
  RECENT_UPDATED: "recent_updated",
  POPULAR: "popular",
} as const;

export type SortOption = (typeof SORT_OPTIONS)[keyof typeof SORT_OPTIONS];

/**
 * User-facing labels for the price sort options.
 *
 * 注意：内部 enum value（`lowest_price` 等）是 API 协议，不要改。
 * 这里集中所有面向用户的中文 label，方便统一管理并满足广告法
 * 对绝对化用语（"最低价" / "最佳" 等）的禁用要求。
 */
export const PRICE_SORT_LABELS: Record<SortOption, string> = {
  recommended: "综合",
  lowest_price: "低价优先",
  recent_updated: "最近降价",
  popular: "热门",
};

export const DEFAULT_PAGE_SIZE = 20;
export const MAX_PAGE_SIZE = 100;
export const DEFAULT_CURRENCY = "CNY";

/**
 * Editorial tags — drive the H5 / 小程序 home page editorial slots
 * (明星单品 / 新品上市 / 经典传承 / 限定臻品) plus the "热销" badge.
 * Operators set these on a per-product basis from admin.
 */
export const EDITORIAL_TAGS = [
  "明星单品",
  "新品",
  "经典款",
  "热销",
  "限定",
] as const;

export type EditorialTag = (typeof EDITORIAL_TAGS)[number];

export const TRUST_LABELS_CN: Record<ChannelType, string> = {
  duty_free: "免税渠道",
  official: "官方",
  brand_authorized: "品牌授权",
  self_operated: "平台自营",
  third_party: "第三方",
};

// ─────────────────────────────────────────────────────────────────────────────
// Admin RBAC — Phase 6A
//
// Role taxonomy + permission matrix are shared so the API can enforce and
// the admin UI can hide controls from the same source of truth. Roles ranked
// roughly by privilege:
//   superadmin — manages admin_users + audit logs + everything else
//   editor     — day-to-day operator: edit products / channels / snapshots
//   viewer     — read-only across business entities, no admin management
//   auditor    — compliance: read audit logs + business read, no writes
// ─────────────────────────────────────────────────────────────────────────────

export const ADMIN_ROLES = {
  SUPERADMIN: "superadmin",
  EDITOR: "editor",
  VIEWER: "viewer",
  AUDITOR: "auditor",
} as const;

export type AdminRole = (typeof ADMIN_ROLES)[keyof typeof ADMIN_ROLES];

export const ADMIN_ROLE_LABELS_CN: Record<AdminRole, string> = {
  superadmin: "超级管理员",
  editor: "编辑",
  viewer: "只读",
  auditor: "审计",
};

/** A single resource's allowed operations under a role. */
export type ResourcePermission = {
  read: boolean;
  write: boolean;
  delete: boolean;
};

/**
 * 9 resources × 3 ops. Kept as plain object literals (no helper) so it is
 * trivial to read from either side of the wire and so TS can statically
 * verify every (role, resource, op) cell is filled.
 *
 * Resource keys:
 *   products            — standard_products + brands + categories (CRUD)
 *   channels            — channels (CRUD)
 *   channelProducts     — channel_products + channel scrapers
 *   priceSnapshots      — price_snapshots (CRUD, mostly cron-driven)
 *   affiliateNetworks   — affiliate_networks + channel_affiliate_configs
 *   homeSettings        — site_settings (home banners / feature)
 *   legalDocs           — legal markdown bodies
 *   adminUsers          — admin_users CRUD (superadmin only)
 *   auditLogs           — audit_logs read (no write — append-only)
 */
/**
 * The full ordered list of permission resources, with Chinese labels. Drives
 * the role-permission matrix UI in the admin and the path→resource enforcement
 * map on the API. Adding a resource here + mapping its routes is all it takes
 * to bring a new area under RBAC.
 */
export const PERMISSION_RESOURCE_KEYS = [
  "products",
  "channels",
  "channelProducts",
  "priceSnapshots",
  "affiliateNetworks",
  "homeSettings",
  "legalDocs",
  "adminUsers",
  "auditLogs",
] as const;

export type PermissionResource = (typeof PERMISSION_RESOURCE_KEYS)[number];

export const PERMISSION_RESOURCE_LABELS_CN: Record<PermissionResource, string> = {
  products: "商品 / 品牌 / 分类",
  channels: "渠道",
  channelProducts: "渠道商品 / 采集",
  priceSnapshots: "价格快照",
  affiliateNetworks: "联盟 / 佣金",
  homeSettings: "站点配置 / 首页",
  legalDocs: "法律文档",
  adminUsers: "账号 / 角色",
  auditLogs: "审计日志",
};

/** A role's full (resource → ops) matrix. */
export type RolePermissions = Record<PermissionResource, ResourcePermission>;

/** A persisted role (system or custom) as stored in `admin_roles`. */
export type AdminRoleRecord = {
  slug: string;
  nameCn: string;
  description: string | null;
  permissions: RolePermissions;
  /** System roles (the original 4) cannot be deleted; superadmin is always all-access. */
  isSystem: boolean;
  sortOrder: number;
};

/** A blank matrix (everything denied) — the starting point for a new custom role. */
export function emptyRolePermissions(): RolePermissions {
  return PERMISSION_RESOURCE_KEYS.reduce((acc, key) => {
    acc[key] = { read: false, write: false, delete: false };
    return acc;
  }, {} as RolePermissions);
}

/** An all-access matrix — what superadmin always resolves to. */
export function fullRolePermissions(): RolePermissions {
  return PERMISSION_RESOURCE_KEYS.reduce((acc, key) => {
    acc[key] = { read: true, write: true, delete: true };
    return acc;
  }, {} as RolePermissions);
}

export const ROLE_PERMISSIONS: Record<AdminRole, RolePermissions> = {
  // Full access — superadmin is the only role that can manage other admins
  // or hard-delete audit logs (the latter is still a no-op route-wise since
  // audit_logs is append-only, but the matrix lists the intent).
  superadmin: {
    products:          { read: true,  write: true,  delete: true  },
    channels:          { read: true,  write: true,  delete: true  },
    channelProducts:   { read: true,  write: true,  delete: true  },
    priceSnapshots:    { read: true,  write: true,  delete: true  },
    affiliateNetworks: { read: true,  write: true,  delete: true  },
    homeSettings:      { read: true,  write: true,  delete: true  },
    legalDocs:         { read: true,  write: true,  delete: true  },
    adminUsers:        { read: true,  write: true,  delete: true  },
    auditLogs:         { read: true,  write: true,  delete: true  },
  },
  // Day-to-day operator. Can edit business content but not delete from
  // safety-critical tables (snapshots history, affiliate configs) and has
  // zero access to admin_users / audit_logs.
  editor: {
    products:          { read: true,  write: true,  delete: true  },
    channels:          { read: true,  write: true,  delete: true  },
    channelProducts:   { read: true,  write: true,  delete: true  },
    priceSnapshots:    { read: true,  write: true,  delete: false },
    affiliateNetworks: { read: true,  write: false, delete: false },
    homeSettings:      { read: true,  write: true,  delete: false },
    legalDocs:         { read: true,  write: false, delete: false },
    adminUsers:        { read: false, write: false, delete: false },
    auditLogs:         { read: false, write: false, delete: false },
  },
  // Read-only everywhere across business entities. Useful for partner / QA
  // accounts that need to see catalog state but never write.
  viewer: {
    products:          { read: true,  write: false, delete: false },
    channels:          { read: true,  write: false, delete: false },
    channelProducts:   { read: true,  write: false, delete: false },
    priceSnapshots:    { read: true,  write: false, delete: false },
    affiliateNetworks: { read: true,  write: false, delete: false },
    homeSettings:      { read: true,  write: false, delete: false },
    legalDocs:         { read: true,  write: false, delete: false },
    adminUsers:        { read: false, write: false, delete: false },
    auditLogs:         { read: false, write: false, delete: false },
  },
  // Compliance reviewer — sees audit logs + business state, no writes.
  auditor: {
    products:          { read: true,  write: false, delete: false },
    channels:          { read: true,  write: false, delete: false },
    channelProducts:   { read: true,  write: false, delete: false },
    priceSnapshots:    { read: true,  write: false, delete: false },
    affiliateNetworks: { read: true,  write: false, delete: false },
    homeSettings:      { read: true,  write: false, delete: false },
    legalDocs:         { read: true,  write: false, delete: false },
    adminUsers:        { read: false, write: false, delete: false },
    auditLogs:         { read: true,  write: false, delete: false },
  },
};

/**
 * Failed-login threshold before the row gets `locked_at` stamped. Picked low
 * enough that a password-guessing attacker (even one defeating the IP rate
 * limit by rotating IPs) burns through the budget within seconds rather than
 * weeks, but high enough that real fat-fingering doesn't lock the operator
 * out. Tune via ops dashboard — adjusting in code requires no migration.
 */
export const ADMIN_LOGIN_LOCKOUT_THRESHOLD = 10;

/**
 * Operator-tunable app config. These were originally hardcoded across H5 and
 * the miniprogram (pinned home channels, the price-drop badge cutoff, the
 * import-tax rate). They now live in `site_settings` under one key so ops can
 * change them from the admin without a redeploy, and both clients read the
 * same source of truth. `DEFAULT_APP_CONFIG` is the fallback when the key is
 * unset and the baseline the admin form resets to.
 */
export const APP_CONFIG_SETTING_KEY = "app_config";

export type AppConfig = {
  /** Channel `code`s pinned (in order) to the front of the home channel rail. */
  pinnedChannelCodes: string[];
  /** Minimum drop percent for a product card to show the "降价" badge. */
  priceDropBadgeThreshold: number;
  /** 行邮税率（百分比），用于「含税到手」计算。 */
  importTaxRatePercent: number;
};

export const DEFAULT_APP_CONFIG: AppConfig = {
  pinnedChannelCodes: ["cdf_japan", "cdf", "hailv", "self"],
  priceDropBadgeThreshold: 15,
  importTaxRatePercent: 13,
};
